import { useEffect, useState } from "react";

// The city dataset is ~1.19 MB of source (32,814 entries, +168 KB gzip). It is
// only ever read by city pickers on authenticated admin/onboarding surfaces, so
// it must not sit in the entry chunk that every visitor — including anonymous
// ones on the prerendered marketing pages — downloads. Importing it dynamically
// puts it in its own chunk, fetched the first time a picker mounts.
//
// The type re-export is type-only, so it is erased at build time and does not
// pull the data module back into the importer's chunk.
export type { USCity } from "@/data/usCities";

import type { USCity } from "@/data/usCities";

const EMPTY: USCity[] = [];

// Module-level cache so the chunk is fetched and parsed once per session, no
// matter how many pickers mount (the profile editor renders several).
let cache: USCity[] | null = null;
let inFlight: Promise<USCity[]> | null = null;

function loadUsCities(): Promise<USCity[]> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = import("@/data/usCities")
      .then((mod) => {
        cache = mod.usCities;
        return cache;
      })
      .catch((err) => {
        // Let a later mount retry (e.g. the user recovers from a flaky network).
        inFlight = null;
        throw err;
      });
  }
  return inFlight;
}

/**
 * Returns the US city list, fetching its chunk on first use.
 *
 * `isLoading` is true only while the chunk is in flight — once it resolves (or
 * fails) callers get a real array and can filter as before. Pickers should let
 * `isLoading` take precedence over their "no results" state so a fast typist
 * doesn't see "No city found" while the data is still arriving.
 */
export function useUsCities(): { cities: USCity[]; isLoading: boolean; error: boolean } {
  const [cities, setCities] = useState<USCity[] | null>(cache);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cities) return;
    let cancelled = false;
    loadUsCities().then(
      (loaded) => {
        if (!cancelled) setCities(loaded);
      },
      () => {
        if (!cancelled) setError(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [cities]);

  return { cities: cities ?? EMPTY, isLoading: !cities && !error, error };
}
